import { Field, MonoLabel, Textarea, Toggle } from '@/design-system';
import { formatMoney, type OrderInvoice } from '@/entities/invoice';
import { INVOICE_MESSAGE_MAX } from '@/entities/tenant';
import { formatDayShort } from '@/shared/lib';

import { reportCopy } from '../copy';
import type { InvoiceOptionState } from '../useInvoiceChoice';

const copy = reportCopy.invoice;

export interface InvoiceEnclosureProps {
    option: InvoiceOptionState;
    /** Er staat een klant met een e-mailadres tussen de ontvangers: alleen die krijgt de factuur. */
    hasClient: boolean;
    /** Ook anderen dan de klant krijgen het rapport; zij krijgen de factuur niet. */
    hasOthers: boolean;
    checked: boolean;
    onCheckedChange: (checked: boolean) => void;
    message: string;
    onMessageChange: (message: string) => void;
    disabled?: boolean;
    /** De inkt van de kaart eromheen: amber in de bevestiging van goedkeuren. */
    tone?: 'warn' | 'neutral';
    /**
     * Waarom de factuur nu niet mee kan, al zou ze dat kunnen: bij een bijgewerkte
     * versie zonder bericht aan de klant gaat er geen mail, en dus geen factuur.
     */
    blocked?: string;
}

/**
 * Het blok "Factuur" in een verstuurdialoog.
 *
 * Kan de factuur mee, dan een schakelaar "Factuur meesturen" met eronder wat er met de
 * factuur gebeurt: een keuring zonder factuur krijgt er een uit haar diensten, een
 * concept wordt definitief met het volgende nummer - dat staat er met zoveel woorden,
 * want het kan niet terug - en een verstuurde factuur gaat mee zoals ze is. Staat de
 * schakelaar aan, dan het tekstveld "Eigen bericht".
 *
 * Kan de factuur niet mee, dan één zin waarom: de klant heeft haar al, ze is betaald,
 * er valt niets te factureren, of er staat geen klant tussen de ontvangers.
 */
export function InvoiceEnclosure({
    option,
    hasClient,
    hasOthers,
    checked,
    onCheckedChange,
    message,
    onMessageChange,
    disabled = false,
    tone = 'neutral',
    blocked,
}: InvoiceEnclosureProps) {
    const ink = tone === 'warn' ? 'text-[var(--warn-ink)]' : 'text-[var(--text-secondary)]';
    const note = `m-0 text-[13px] leading-[1.5] ${ink}`;

    return (
        <section aria-label={copy.label} className="mt-[14px] max-w-[620px]">
            <MonoLabel
                size={10}
                tone={tone === 'warn' ? 'warn' : 'muted'}
                as="div"
                className="mb-1"
            >
                {copy.label}
            </MonoLabel>
            {content()}
        </section>
    );

    function content() {
        if (option === undefined) {
            return (
                <p role="status" className={note}>
                    {copy.loading}
                </p>
            );
        }

        if (option === null) {
            return <p className={note}>{copy.unknown}</p>;
        }

        if (!option.enclosable) {
            return <p className={note}>{unavailable(option)}</p>;
        }

        if (!hasClient) {
            return <p className={note}>{copy.noClient}</p>;
        }

        if (blocked !== undefined) {
            return <p className={note}>{blocked}</p>;
        }

        return (
            <>
                <Toggle
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={onCheckedChange}
                    label={copy.toggle}
                    description={checked ? whatHappens(option) : copy.off}
                />
                {checked ? (
                    <>
                        <p className={note}>
                            {hasOthers ? `${copy.mail} ${copy.onlyClient}` : copy.mail}
                        </p>
                        <Field
                            className="mt-3"
                            label={copy.message}
                            description={`${copy.messageHint} ${copy.messageCount(message.length, INVOICE_MESSAGE_MAX)}`}
                        >
                            <Textarea
                                value={message}
                                rows={4}
                                maxLength={INVOICE_MESSAGE_MAX}
                                disabled={disabled}
                                placeholder={copy.messagePlaceholder}
                                onChange={(event) => onMessageChange(event.target.value)}
                            />
                        </Field>
                    </>
                ) : null}
            </>
        );
    }
}

/** Wat versturen met de factuur doet, voor deze stand. */
function whatHappens(option: OrderInvoice): string {
    if (option.state === 'none') {
        return copy.none;
    }

    if (option.state === 'draft') {
        return copy.draft(formatMoney(option.total));
    }

    return copy.issued(option.number ?? option.label ?? '', formatMoney(option.total));
}

/** Waarom de factuur niet mee kan. */
function unavailable(option: OrderInvoice): string {
    if (option.state === 'delivered') {
        return copy.delivered(
            option.number ?? option.label ?? '',
            option.mailedAt === null ? null : formatDayShort(option.mailedAt),
        );
    }

    return option.state === 'nothing_to_invoice' ? copy.nothing : copy.closed;
}
